import { FC, useEffect, useState } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useHistory } from 'react-router';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { observer } from 'mobx-react-lite';
import { BasePathInput } from 'components/BasePathInput';
import {
  Paper,
  FormControlLabel,
  Container,
  Grid,
  FormControl,
  Checkbox,
  TextField,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
} from '@material-ui/core';
import { useStyles } from './styles';
import { GroupObjectFormInputPattern, GroupData } from './types';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { Autocomplete } from '@mui/material';

export const GroupObjectForm: FC<any> = observer(({ type, currentGroup }) => {
  const classes = useStyles();
  const history = useHistory();
  const [isAppStatusChecked, setisAppStatusChecked] = useState<boolean>(false);
  const [options, setOptions] = useState<string[]>([]);
  const [removedOption, setRemovedOption] = useState<string[]>([]);
  const [selectedManagers, setSelectedManagers] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [adminsData, setAdminsData] = useState<any[]>([]);
  const {
    ldapStore: { updateGroup, createGroup, getGroups, usersView, getUsers },
    AppStore: { providerList },
    adminsTableStore: { getAdmins, adminsView },
    notificationStore: { openNotification },
    baseTreeExplorerStore: { selectedBase },
  } = useStore();
  const providerListReq = useQuery('providerListReq', () => providerList(), {
    onError: () => openNotification('error', `We are unable to connect `),
  });
  const usersReq = useQuery('usersReq', () => getUsers(), {
    onSuccess: (data) => {
      const userData = data || usersView || [];
      setUsersData(userData);
    },
    onError: (error: any) => {
      console.error('Users fetch error:', error);
      openNotification('error', error);
    },
  });

  const adminsReq = useQuery('adminsReq', () => getAdmins(), {
    onSuccess: (data) => {
      const adminData = data || adminsView || [];
      setAdminsData(adminData);
    },
    onError: (error: any) => openNotification('error', error),
  });
  const providerDisplayMap: { [key: string]: string } = {
    google: 'Google Workspace',
    zoom: 'Zoom Workspace',
    microsoft: 'Microsoft Workspace',
  };
  useEffect(() => {
    if (providerListReq.data) {
      const filteredOptions = providerListReq.data.filter(
        (app: { token: any; tokenStatus: string; provider: string }) =>
          app.token && app.tokenStatus === 'active' && app.provider !== 'github'
      );
      const optionView = filteredOptions.map(
        (item: { provider: any }) => providerDisplayMap[item.provider] || item.provider
      );
      setOptions(optionView);
      if (currentGroup?.reSyncProvider.length !== 0 && currentGroup?.reSyncProvider.length !== undefined) {
        setisAppStatusChecked(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerListReq.data]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(currentGroup?.reSyncProvider || []);
  const [openPopup, setOpenPopup] = useState(false);
  const [optionToUncheck, setOptionToUncheck] = useState<string | null>(null);
  const [previousSelectedOptions, setPreviousSelectedOptions] = useState<string[]>([]);
  const [confirm, setConfirm] = useState('no');
  const [isCancelClicked, setIsCancelClicked] = useState(false);
  useEffect(() => {
    if (currentGroup?.reSyncProvider) {
      setSelectedOptions(currentGroup.reSyncProvider);
    }
  }, [currentGroup]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, option: string) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setSelectedOptions((prevOptions: string[]) => {
        if (Array.isArray(prevOptions)) {
          if (!prevOptions.includes(option)) {
            return [...prevOptions, option];
          }
        }
        return prevOptions;
      });
    } else {
      setPreviousSelectedOptions(selectedOptions);
      setOptionToUncheck(option);
      setOpenPopup(true);
    }
  };
  const handleConfirm = () => {
    if (optionToUncheck) {
      setSelectedOptions((prevOptions: any[]) => prevOptions.filter((item: string) => item !== optionToUncheck));
      setRemovedOption((prev) => [...prev, optionToUncheck]);
      setOptionToUncheck(null);
      setConfirm('yes');
    }
    setOpenPopup(false);
  };
  const handleCancel = () => {
    setSelectedOptions(previousSelectedOptions);
    setOptionToUncheck(null);
    setOpenPopup(false);
    setIsCancelClicked(true);
  };
  const updateGroupReq = useMutation((data: GroupData) => updateGroup(data), {
    mutationKey: 'updateGroupReq',
    onSuccess: async () => {
      await getGroups();
      openNotification('success', 'Saved!!');
      history.push(handleCompanyUrl('/groups'));
    },
    onError: () => openNotification('error', `Can't update group data`),
  });
  const createGroupReq = useMutation((data: GroupData) => createGroup(data), {
    mutationKey: 'createGroupReq',
    onSuccess: async (response) => {
      await getGroups();
      openNotification('success', 'Created Successfully');
      history.push(handleCompanyUrl('/groups'));
    },
    onError: (error) => {
      openNotification('error', `${error}`);
    },
  });

  useAppPreloader(
    updateGroupReq.isLoading ||
      createGroupReq.isLoading ||
      providerListReq.isFetching ||
      usersReq.isFetching ||
      adminsReq.isFetching
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<GroupData>();

  // Effect to match and select managers from the provided list
  useEffect(() => {
    if (currentGroup?.managers && (usersData.length > 0 || adminsData.length > 0)) {
      const allUsers = [...usersData, ...adminsData];
      const matchedManagers = currentGroup.managers
        .map((manager: any) => {
          // Find matching user by user_id, firstName+secondName, or email
          return allUsers.find(
            (user) =>
              user.user_id === manager.user_id ||
              (user.cn && user.cn.toLowerCase().includes(manager.firstName?.toLowerCase())) ||
              user.mail === manager.email ||
              user.email === manager.email
          );
        })
        .filter(Boolean); // Remove null/undefined matches

      if (matchedManagers.length > 0) {
        setSelectedManagers(matchedManagers);
        setValue('manager_id', matchedManagers);
      }
    }
  }, [usersData, adminsData, currentGroup?.managers, setValue]);
  const onSubmit = (data: GroupData) => {
    // Convert manager objects to user IDs before submission
    if (data.manager_id && Array.isArray(data.manager_id)) {
      data.manager_id = data.manager_id.map((manager: any) => {
        if (typeof manager === 'object') {
          return manager.user_id || null; // Send user_id or null if not available
        }
        return manager; // If it's already a string, keep it
      });
    }

    data.reSyncProvider = isAppStatusChecked === false ? [] : selectedOptions;
    data.deleteProviderType = isAppStatusChecked === false ? 'yes' : isCancelClicked ? 'no' : confirm;
    data.deleteProvider =
      isAppStatusChecked === false
        ? selectedOptions.length === 0
          ? removedOption
          : selectedOptions
        : isCancelClicked
        ? []
        : removedOption;
    (!!currentGroup as boolean) && currentGroup.google_id && (data.googleId = currentGroup.google_id);
    (!!currentGroup as boolean) && currentGroup.zoom_id && (data.zoomId = currentGroup.zoom_id);
    data.microsoftId =
      (!!currentGroup && currentGroup.microsoft_id) || isAppStatusChecked === false
        ? (data.microsoftId = currentGroup?.microsoft_id)
        : '';
    (!!currentGroup as boolean) && (data.email = currentGroup.email);
    const updatedFormData = {
      ...data,
      appStatus: isAppStatusChecked,
    };
    (!!currentGroup as boolean) && currentGroup.google_id && (data.email = currentGroup.email);
    (!!currentGroup as boolean) ? updateGroupReq.mutate(updatedFormData) : createGroupReq.mutate(updatedFormData);
  };

  const mockDataProperties: GroupObjectFormInputPattern[] = [
    {
      label: 'Group Name',
      key: 'cn',
      required: true,
      autoFocus: true,
      placeholder: 'Enter group name',
    },
    { label: 'Description', key: 'description', required: true, placeholder: 'Enter group description' },
    { label: 'Dn', key: 'dn', required: false, disable: true },
    { label: 'Active Till', key: 'end_date', required: false, type: 'date' },
    // { label: 'Manager', key: 'manager_id', required: false, type: 'multiselect' },
  ];
  const mockDataProperties1: GroupObjectFormInputPattern[] = [
    { label: 'Manager', key: 'manager_id', required: false, type: 'multiselect' },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.box}>
      <Grid container justify='space-between'>
        <Paper className={classes.paper}>
          <p className={classes.boxTitle}>Group Properties</p>
          <Container className={classes.tableBox}>
            <Controller
              name='base'
              defaultValue={selectedBase}
              control={control}
              render={({ field }) => (
                <Grid container className={classes.row} style={{ display: 'none' }}>
                  <BasePathInput disabled={type === 'view'} {...field} />
                </Grid>
              )}
            />
            {mockDataProperties.map((item, index) => (
              <Controller
                key={item.key}
                name={item.key}
                defaultValue={
                  item.key === 'manager_id' ? selectedManagers : item.key ? currentGroup?.[item.key] : undefined
                }
                control={control}
                rules={{ required: item?.required }}
                render={({ field, fieldState: { invalid, error } }) => (
                  <Grid key={index} container className={classes.row}>
                    <p className={classes.title}>{item.label}</p>
                    {item.type === 'date' ? (
                      <TextField
                        {...field}
                        type='date'
                        error={invalid}
                        disabled={type === 'view' || item.disable}
                        helperText={invalid ? 'Required field' : ''}
                        className={classes.input}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : item.type === 'multiselect' ? (
                      <FormControl fullWidth>
                        <Autocomplete
                          multiple
                          options={[...usersData, ...adminsData]}
                          value={Array.isArray(field.value) ? field.value : selectedManagers}
                          disablePortal
                          onChange={(event, newValue) => {
                            const plainObjects = newValue.map((item) => {
                              if (item && typeof item === 'object') {
                                return {
                                  dn: item.dn,
                                  uid: item.uid,
                                  cn: item.cn,
                                  sn: item.sn,
                                  mail: item.mail,
                                  base: item.base,
                                  user_id: item.user_id,
                                };
                              }
                              return item;
                            });
                            field.onChange(plainObjects);
                            // Update selectedManagers state to keep it in sync
                            if (item.key === 'manager_id') {
                              setSelectedManagers(plainObjects);
                            }
                          }}
                          disabled={type === 'view'}
                          getOptionLabel={(option) => {
                            return option?.cn || option?.uid || 'Unknown User';
                          }}
                          isOptionEqualToValue={(option, value) => {
                            if (!option || !value) return false;
                            return option.dn === value.dn || option.uid === value.uid || option.mail === value.mail;
                          }}
                          loading={usersReq.isLoading}
                          noOptionsText='No users found'
                          renderOption={(props, option) => {
                            const selectedValues = Array.isArray(field.value) ? field.value : selectedManagers;

                            const isSelected = selectedValues.some((selected: any) => {
                              // Handle both object and primitive comparisons
                              if (typeof selected === 'object' && selected !== null) {
                                return (
                                  (selected.dn && selected.dn === option.dn) ||
                                  (selected.uid && selected.uid === option.uid) ||
                                  (selected.mail && selected.mail === option.mail) ||
                                  (selected.user_id && selected.user_id === option.user_id)
                                );
                              }
                              // Handle primitive values (dn, uid, or email)
                              return selected === option.dn || selected === option.uid || selected === option.mail;
                            });

                            const displayName = option?.cn || option?.uid || 'Unknown User';

                            return (
                              <li {...props}>
                                <Checkbox checked={isSelected} />
                                <ListItemText primary={displayName} />
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField {...params} variant='outlined' disabled={type === 'view'} />
                          )}
                        />
                      </FormControl>
                    ) : (
                      <TextField
                        {...field}
                        autoFocus={item?.autoFocus}
                        error={invalid}
                        disabled={type === 'view' || item.disable}
                        multiline={item?.multiline}
                        rows={item?.multiline ? 4 : 1}
                        placeholder={item?.placeholder}
                        helperText={
                          invalid ? (error?.type === 'pattern' ? item.pattern?.errorMessage : 'Required field') : ''
                        }
                        className={classes.input}
                      />
                    )}
                    <div className={classes.actionBox}>{item.actions}</div>
                  </Grid>
                )}
              />
            ))}
          </Container>
        </Paper>
        {type !== 'view' && (
          <Paper className={classes.paper}>
            <div style={{ marginBottom: '20px' }}>
              <FormControlLabel
                className={classes.notify}
                control={
                  <Controller
                    name='appStatus'
                    control={control}
                    defaultValue={isAppStatusChecked}
                    render={({ field }) => (
                      <Checkbox
                        disabled={type === 'view'}
                        defaultChecked={isAppStatusChecked}
                        checked={isAppStatusChecked}
                        onChange={(e) => {
                          setisAppStatusChecked(e.target.checked);
                          field.onChange(e.target.checked);
                        }}
                        name='appStatus'
                      />
                    )}
                  />
                }
                label='Assign Connected Apps to Group'
              />
            </div>
            <Container className={classes.tableBox}>
              <p className={classes.boxTitle}>Select Connected Apps</p>
              <Grid className={classes.row} container justify='space-between'>
                <FormControl fullWidth>
                  <Controller
                    name='reSyncProvider'
                    control={control}
                    rules={{
                      required: isAppStatusChecked && selectedOptions.length === 0,
                    }}
                    render={({ field }) => (
                      <>
                        <Autocomplete
                          multiple
                          value={selectedOptions.map((option) => providerDisplayMap[option] || option)}
                          onChange={(event, newValue) => {
                            const selectedProviderNames = newValue.map((displayName) => {
                              return (
                                Object.keys(providerDisplayMap).find(
                                  (key) => providerDisplayMap[key] === displayName
                                ) || displayName
                              );
                            });
                            const lastSelectedOption = selectedProviderNames.find(
                              (option) => !selectedOptions.includes(option)
                            );
                            const lastRemovedOption = selectedOptions.find(
                              (option) => !selectedProviderNames.includes(option)
                            );
                            if (lastSelectedOption) {
                              handleCheckboxChange(
                                { target: { checked: true } } as React.ChangeEvent<HTMLInputElement>,
                                lastSelectedOption
                              );
                            }
                            if (lastRemovedOption) {
                              handleCheckboxChange(
                                { target: { checked: false } } as React.ChangeEvent<HTMLInputElement>,
                                lastRemovedOption
                              );
                            }
                            setSelectedOptions(selectedProviderNames);
                            field.onChange(newValue);
                          }}
                          disabled={type === 'view' || isAppStatusChecked === false}
                          renderOption={(props, option) => {
                            const providerName =
                              Object.keys(providerDisplayMap).find((key) => providerDisplayMap[key] === option) ||
                              option;
                            return (
                              <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                  checked={selectedOptions.includes(providerName)}
                                  onChange={(event) => handleCheckboxChange(event, providerName)}
                                  style={{ marginRight: 8 }}
                                />
                                <ListItemText primary={option} />
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant='outlined'
                              error={!!errors.reSyncProvider && isAppStatusChecked && selectedOptions.length === 0}
                              helperText={
                                !!errors.reSyncProvider && isAppStatusChecked && selectedOptions.length === 0
                                  ? 'Please select at least one option'
                                  : ''
                              }
                              disabled={type === 'view'}
                            />
                          )}
                          options={options}
                        />
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              <Dialog
                open={openPopup}
                onClose={handleCancel}
                aria-labelledby='confirm-dialog-title'
                aria-describedby='confirm-dialog-description'
              >
                <DialogTitle>
                  As you have unselected synchronization, do you want to delete this option from all unchecked
                  third-party apps?
                </DialogTitle>
                <DialogActions>
                  <Button onClick={handleCancel} color='primary'>
                    Cancel
                  </Button>
                  <Button onClick={handleConfirm} color='primary'>
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>
              <Controller
                name='base'
                defaultValue={selectedBase}
                control={control}
                render={({ field }) => (
                  <Grid container className={classes.row} style={{ display: 'none' }}>
                    <BasePathInput disabled={type === 'view'} {...field} />
                  </Grid>
                )}
              />
              {mockDataProperties1.map((item, index) => (
                <Controller
                  key={item.key}
                  name={item.key}
                  defaultValue={
                    item.key === 'manager_id' ? selectedManagers : item.key ? currentGroup?.[item.key] : undefined
                  }
                  control={control}
                  rules={{ required: item?.required }}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <Grid key={index} container className={classes.row}>
                      <p className={classes.boxsTitle}>{item.label}</p>
                      {item.type === 'multiselect' ? (
                        <FormControl fullWidth>
                          <Autocomplete
                            multiple
                            options={[...usersData, ...adminsData]}
                            value={Array.isArray(field.value) ? field.value : selectedManagers}
                            disablePortal
                            onChange={(event, newValue) => {
                              const plainObjects = newValue.map((item) => {
                                if (item && typeof item === 'object') {
                                  return {
                                    dn: item.dn,
                                    uid: item.uid,
                                    cn: item.cn,
                                    sn: item.sn,
                                    mail: item.mail,
                                    base: item.base,
                                    user_id: item.user_id,
                                  };
                                }
                                return item;
                              });
                              field.onChange(plainObjects);
                              // Update selectedManagers state to keep it in sync
                              if (item.key === 'manager_id') {
                                setSelectedManagers(plainObjects);
                              }
                            }}
                            disabled={type === 'view'}
                            getOptionLabel={(option) => {
                              return option?.cn || option?.uid || 'Unknown User';
                            }}
                            isOptionEqualToValue={(option, value) => {
                              if (!option || !value) return false;
                              return option.dn === value.dn || option.uid === value.uid || option.mail === value.mail;
                            }}
                            loading={usersReq.isLoading}
                            noOptionsText='No users found'
                            renderOption={(props, option) => {
                              const selectedValues = Array.isArray(field.value) ? field.value : selectedManagers;

                              const isSelected = selectedValues.some((selected: any) => {
                                // Handle both object and primitive comparisons
                                if (typeof selected === 'object' && selected !== null) {
                                  return (
                                    (selected.dn && selected.dn === option.dn) ||
                                    (selected.uid && selected.uid === option.uid) ||
                                    (selected.mail && selected.mail === option.mail) ||
                                    (selected.user_id && selected.user_id === option.user_id)
                                  );
                                }
                                // Handle primitive values (dn, uid, or email)
                                return selected === option.dn || selected === option.uid || selected === option.mail;
                              });

                              const displayName = option?.cn || option?.uid || 'Unknown User';

                              return (
                                <li {...props}>
                                  <Checkbox checked={isSelected} />
                                  <ListItemText primary={displayName} />
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField {...params} variant='outlined' disabled={type === 'view'} />
                            )}
                          />
                        </FormControl>
                      ) : (
                        <TextField
                          {...field}
                          autoFocus={item?.autoFocus}
                          error={invalid}
                          disabled={type === 'view' || item.disable}
                          multiline={item?.multiline}
                          rows={item?.multiline ? 4 : 1}
                          placeholder={item?.placeholder}
                          helperText={
                            invalid ? (error?.type === 'pattern' ? item.pattern?.errorMessage : 'Required field') : ''
                          }
                          className={classes.input}
                        />
                      )}
                      <div className={classes.actionBox}>{item.actions}</div>
                    </Grid>
                  )}
                />
              ))}
            </Container>
          </Paper>
        )}
      </Grid>

      {type === 'view' ? null : (
        <div className={classes.btnBox}>
          <Button
            type='button'
            variant='contained'
            color='primary'
            className={classes.btnWideLeft}
            onClick={() => history.push(handleCompanyUrl('/groups'))}
          >
            Cancel
          </Button>

          <Button type='submit' variant='contained' color='secondary' className={classes.btnWide}>
            {(!!currentGroup as boolean) ? 'Save Changes' : 'Create'}
          </Button>
        </div>
      )}
    </form>
  );
});
